/**
 * jsPDF font loader — registers FreeSans (subset) so the Indian Rupee (₹)
 * symbol renders correctly in receipt PDFs.
 *
 * Usage:
 *   import { ensureRupeeFont, RUPEE_FONT } from '@/utils/jspdfFonts';
 *   await ensureRupeeFont(doc);
 *   doc.setFont(RUPEE_FONT, 'normal');
 *   doc.text('₹ 1,54,100', 20, 30);
 */
export const RUPEE_FONT = 'FreeSans';
const FONT_URL = '/fonts/FreeSans-subset.ttf';
let cachedBase64 = null;

async function loadFontBase64() {
  if (cachedBase64) return cachedBase64;
  const res = await fetch(FONT_URL);
  if (!res.ok) throw new Error(`Failed to load font: ${res.status}`);
  const buffer = await res.arrayBuffer();
  // Convert ArrayBuffer to base64
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  cachedBase64 = btoa(binary);
  return cachedBase64;
}

/**
 * Registers FreeSans font with the given jsPDF document. Idempotent within a doc.
 * Falls back silently to default helvetica on failure (₹ may render as ?).
 */
export async function ensureRupeeFont(doc) {
  try {
    const fontList = doc.getFontList();
    if (fontList[RUPEE_FONT]) return RUPEE_FONT;

    const b64 = await loadFontBase64();
    doc.addFileToVFS('FreeSans-subset.ttf', b64);
    doc.addFont('FreeSans-subset.ttf', RUPEE_FONT, 'normal');
    doc.addFont('FreeSans-subset.ttf', RUPEE_FONT, 'bold');
    return RUPEE_FONT;
  } catch (e) {
    console.warn('[jspdfFonts] Failed to register FreeSans, falling back to helvetica:', e);
    return 'helvetica';
  }
}

/**
 * Format a number using Indian comma grouping (lakh/crore).
 *   154100  -> '1,54,100'
 *   1234567 -> '12,34,567'
 *   1234.5  -> '1,234.50' (decimals=2)
 */
export function formatINR(value, decimals = 0) {
  if (value === null || value === undefined || value === '') return '0';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
